if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        this.verticalThumbnailAutoscrollMode =
          this.dataset.verticalThumbnailAutoscrollMode === 'center' ? 'center' : 'nearest';
        this.onWindowResize = debounce(this.syncVerticalThumbnailRail.bind(this), 120);
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      connectedCallback() {
        if (!this.elements.thumbnails) return;
        window.addEventListener('resize', this.onWindowResize);
        this.mql.addEventListener?.('change', this.onWindowResize);
        this.syncVerticalThumbnailRail();
      }

      disconnectedCallback() {
        if (!this.elements.thumbnails) return;
        window.removeEventListener('resize', this.onWindowResize);
        this.mql.removeEventListener?.('change', this.onWindowResize);
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
        this.syncVerticalThumbnailRail();
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        const activeMediaPosition = activeThumbnail?.dataset?.mediaPosition || '1';
        this.announceLiveRegion(activeMedia, activeMediaPosition);
        this.syncVerticalThumbnailRail();
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);

        if (this.isVerticalThumbnailLayout()) {
          thumbnail.scrollIntoView({
            block: this.verticalThumbnailAutoscrollMode,
            inline: 'nearest',
            behavior: 'smooth',
          });
          return;
        }

        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      isVerticalThumbnailLayout() {
        return this.dataset.desktopLayout === 'thumbnail_left_vertical' && this.mql.matches;
      }

      syncVerticalThumbnailRail() {
        if (!this.elements.thumbnails || !this.elements.viewer) return;

        if (!this.isVerticalThumbnailLayout()) {
          this.elements.thumbnails.style.removeProperty('--vertical-thumbnail-rail-height');
          this.elements.thumbnails.style.removeProperty('height');
          this.elements.thumbnails.style.removeProperty('max-height');
          return;
        }

        const activeMedia =
          this.elements.viewer.querySelector('.product__media-item.is-active') ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) return;

        const mediaHeight = Math.round(activeMedia.getBoundingClientRect().height);
        if (mediaHeight <= 0) return;

        const railHeight = `${mediaHeight}px`;
        this.elements.thumbnails.style.setProperty('--vertical-thumbnail-rail-height', railHeight);
        this.elements.thumbnails.style.height = railHeight;
        this.elements.thumbnails.style.maxHeight = railHeight;

        const activeImage = activeMedia.querySelector('.product__modal-opener--image img');
        if (activeImage && !activeImage.complete) {
          activeImage.addEventListener('load', () => this.syncVerticalThumbnailRail(), { once: true });
        }
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
